// hooks/useHighlightingSystem.js
import { useState, useEffect, useCallback, useRef } from "react";
import { highlightService } from "../components/services/highlightService";

export function useHighlightingSystem(containerRef, pageNumber, selectionRects, highlightMode, highlights, onHighlightsChange, documentTitle, documentKey) {
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  
  // Refs to track state and prevent duplicates
  const lastSavedHash = useRef('');
  const isSaving = useRef(false);
  const lastSaveTimeout = useRef(null);

  // Generate a unique hash for highlights to detect changes
  const generateHighlightsHash = (highlightsArray) => {
    return highlightsArray
      .map(h => `${h.id}-${h.normalizedTop}-${h.normalizedLeft}-${h.normalizedWidth}-${h.normalizedHeight}`)
      .sort()
      .join('|');
  };

  // RESET when documentKey changes
  useEffect(() => {
    console.log('🔄 Document key changed, resetting highlighting system:', documentKey);
    setHasInitialLoad(false);
    lastSavedHash.current = '';
    isSaving.current = false;
    setIsLoading(false);
    
    // Clear any pending save timeout
    if (lastSaveTimeout.current) {
      clearTimeout(lastSaveTimeout.current);
      lastSaveTimeout.current = null;
    }
  }, [documentKey]);

  // Load highlights when document changes
  useEffect(() => {
    if (!documentTitle || hasInitialLoad) {
      return;
    }

    const loadHighlights = async () => {
      setIsLoading(true);
      try {
        const savedHighlights = await highlightService.getHighlights(documentTitle);
        
        if (savedHighlights && savedHighlights.length > 0) {
          const transformedHighlights = savedHighlights.map(h => {
            const bounds = h.bounds || {};
            const highlight = {
              id: h.id,
              pageNumber: h.pageNumber || 1,
              normalizedTop: bounds.y !== undefined ? bounds.y : (h.normalizedTop || 0),
              normalizedLeft: bounds.x !== undefined ? bounds.x : (h.normalizedLeft || 0),
              normalizedWidth: bounds.width !== undefined ? bounds.width : (h.normalizedWidth || 0),
              normalizedHeight: bounds.height !== undefined ? bounds.height : (h.normalizedHeight || 0),
              textContent: h.textContent || '',
              color: h.color || 'rgba(255, 235, 59, 0.5)',
              createdAt: h.createdAt || new Date().toISOString()
            };
            
            return highlight;
          });
          
          lastSavedHash.current = generateHighlightsHash(transformedHighlights);
          onHighlightsChange(transformedHighlights);
          setHasInitialLoad(true);
        } else {
          lastSavedHash.current = '';
          setHasInitialLoad(true);
        }
      } catch (error) {
        console.error('❌ Failed to load highlights:', error);
        setHasInitialLoad(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadHighlights();
  }, [documentTitle, onHighlightsChange, hasInitialLoad]);

  // Save highlights when they change
  useEffect(() => {
    // Don't save on initial load or if no document title
    if (!hasInitialLoad || !documentTitle || isSaving.current) {
      return;
    }

    const currentHash = generateHighlightsHash(highlights);
    
    // Don't save if highlights haven't actually changed
    if (currentHash === lastSavedHash.current) {
      return;
    }

    // Don't save empty arrays (unless we're intentionally clearing)
    if (highlights.length === 0 && lastSavedHash.current === '') {
      return;
    }

    // Clear any existing timeout
    if (lastSaveTimeout.current) {
      clearTimeout(lastSaveTimeout.current);
    }

    // Set new timeout with debouncing
    lastSaveTimeout.current = setTimeout(async () => {
      // Prevent multiple simultaneous saves
      if (isSaving.current) {
        return;
      }

      isSaving.current = true;
      
      try {
        // Remove any potential duplicates before saving
        const uniqueHighlights = highlights.filter((highlight, index, array) => 
          array.findIndex(h => 
            h.id === highlight.id && 
            h.normalizedTop === highlight.normalizedTop &&
            h.normalizedLeft === highlight.normalizedLeft
          ) === index
        );

        // Transform frontend format to backend format
        const highlightsToSave = uniqueHighlights.map(highlight => ({
          id: highlight.id,
          pageNumber: highlight.pageNumber || 1,
          bounds: {
            x: highlight.normalizedLeft || 0,
            y: highlight.normalizedTop || 0,
            width: highlight.normalizedWidth || 0,
            height: highlight.normalizedHeight || 0
          },
          textContent: highlight.textContent || '',
          color: highlight.color || 'rgba(255, 235, 59, 0.5)',
          createdAt: highlight.createdAt || new Date().toISOString()
        }));
        
        const success = await highlightService.saveHighlights(documentTitle, highlightsToSave);
        
        if (success) {
          // Update the hash only after successful save
          lastSavedHash.current = generateHighlightsHash(highlights);
        }
      } catch (error) {
        console.error('❌ Error saving highlights:', error);
      } finally {
        isSaving.current = false;
      }
    }, 2000); // 2 second debounce

    return () => {
      if (lastSaveTimeout.current) {
        clearTimeout(lastSaveTimeout.current);
        lastSaveTimeout.current = null;
      }
    };
  }, [highlights, documentTitle, hasInitialLoad]);

  // Add highlight
  const addHighlight = useCallback((textContent) => {
    if (!selectionRects.length) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const textLayer = container.querySelector(
      `.react-pdf__Page[data-page-number="${pageNumber}"] .react-pdf__Page__textContent`
    );
    
    if (!textLayer) {
      return;
    }

    const layerRect = textLayer.getBoundingClientRect();

    // Calculate coordinates relative to text layer WITHOUT scroll offsets
    const normalizedRects = selectionRects
      .map((r, index) => {
        const topRelative = r.top - layerRect.top;
        const leftRelative = r.left - layerRect.left;
        
        const normalizedRect = {
          id: `highlight-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
          normalizedTop: topRelative / layerRect.height,
          normalizedLeft: leftRelative / layerRect.width,
          normalizedWidth: r.width / layerRect.width,
          normalizedHeight: r.height / layerRect.height,
          pageNumber,
          textContent: textContent || '',
          color: 'rgba(255, 235, 59, 0.5)',
          createdAt: new Date().toISOString()
        };
        
        return normalizedRect;
      })
      .filter((r) => {
        const isValid = 
          r.normalizedWidth > 0.001 &&
          r.normalizedHeight > 0.001 &&
          r.normalizedTop >= 0 &&
          r.normalizedTop <= 1 &&
          r.normalizedLeft >= 0 &&
          r.normalizedLeft <= 1;
        return isValid;
      });

    if (!normalizedRects.length) {
      return;
    }

    // Check for duplicates before adding
    const existingHighlightIds = new Set(highlights.map(h => h.id));
    const newHighlightsToAdd = normalizedRects.filter(rect => !existingHighlightIds.has(rect.id));
    
    if (newHighlightsToAdd.length === 0) {
      return;
    }

    const newHighlights = [...highlights, ...newHighlightsToAdd];
    onHighlightsChange(newHighlights);
    
    // Clear the selection after creating highlight
    window.getSelection()?.removeAllRanges();
  }, [containerRef, pageNumber, selectionRects, highlights, onHighlightsChange]);

  const clearHighlights = useCallback(() => {
    lastSavedHash.current = ''; // Reset hash when clearing
    onHighlightsChange([]);
  }, [onHighlightsChange]);

  const clearCurrentPageHighlights = useCallback(() => {
    const filteredHighlights = highlights.filter((h) => h.pageNumber !== pageNumber);
    onHighlightsChange(filteredHighlights);
  }, [highlights, pageNumber, onHighlightsChange]);

 // In useHighlightingSystem.js, update the delete functions:

// Delete single highlight
const deleteHighlight = useCallback(async (highlightId) => {
  // Update local state immediately
  const filteredHighlights = highlights.filter((h) => h.id !== highlightId);
  onHighlightsChange(filteredHighlights);
  
  // Also delete from backend
  if (documentTitle) {
    try {
      await highlightService.deleteHighlight(documentTitle, highlightId);
    } catch (error) {
      console.error('Failed to delete highlight from backend:', error);
    }
  }
}, [highlights, onHighlightsChange, documentTitle]);

// Delete multiple highlights at once
const deleteMultipleHighlights = useCallback(async (highlightIds) => {
  if (!highlightIds || highlightIds.length === 0) {
    return;
  }

  // Update local state immediately
  const highlightIdsSet = new Set(highlightIds);
  const filteredHighlights = highlights.filter((h) => !highlightIdsSet.has(h.id));
  onHighlightsChange(filteredHighlights);
  
  // Also delete from backend
  if (documentTitle) {
    
      // If bulk delete fails, try deleting one by one
      try {
        const deletePromises = highlightIds.map(id => 
          highlightService.deleteHighlight(documentTitle, id)
        );
        await Promise.allSettled(deletePromises);
      } catch (fallbackError) {
        console.error('Fallback delete also failed:', fallbackError);
      }
    }
  }, [highlights, onHighlightsChange, documentTitle]);
  // Refresh highlights from backend
  const refreshHighlights = useCallback(async () => {
    if (!documentTitle) {
      return;
    }

    setIsLoading(true);
    try {
      const savedHighlights = await highlightService.getHighlights(documentTitle);
      
      if (savedHighlights && savedHighlights.length > 0) {
        const transformedHighlights = savedHighlights.map(h => {
          const bounds = h.bounds || {};
          return {
            id: h.id,
            pageNumber: h.pageNumber || 1,
            normalizedTop: bounds.y !== undefined ? bounds.y : (h.normalizedTop || 0),
            normalizedLeft: bounds.x !== undefined ? bounds.x : (h.normalizedLeft || 0),
            normalizedWidth: bounds.width !== undefined ? bounds.width : (h.normalizedWidth || 0),
            normalizedHeight: bounds.height !== undefined ? bounds.height : (h.normalizedHeight || 0),
            textContent: h.textContent || '',
            color: h.color || 'rgba(255, 235, 59, 0.5)',
            createdAt: h.createdAt || new Date().toISOString()
          };
        });
        
        lastSavedHash.current = generateHighlightsHash(transformedHighlights);
        onHighlightsChange(transformedHighlights);
      } else {
        lastSavedHash.current = '';
        onHighlightsChange([]);
      }
    } catch (error) {
      console.error('❌ Failed to refresh highlights:', error);
    } finally {
      setIsLoading(false);
    }
  }, [documentTitle, onHighlightsChange]);

  return { 
    highlights, 
    addHighlight, 
    clearHighlights, 
    clearCurrentPageHighlights,
    deleteHighlight,
    deleteMultipleHighlights,
    refreshHighlights,
    isLoading
  };
}